import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Star, MapPin, Plus, Trash2, Settings, QrCode, 
  Building2, Heart, User, ExternalLink,
  ChevronDown, ChevronUp, Copy, Check, Zap, TrendingUp, Crown,
  Users, BarChart3, CreditCard, Ban, CheckCircle, RefreshCw,
  Eye, Mail, Phone, LogOut, Send, Clock, Mic, Target, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_INFO = {
  BASIC: { name: 'Starter', limit: null, icon: Zap, color: 'teal', price: '₹999/mo' },
  GROWTH: { name: 'Growth', limit: null, icon: TrendingUp, color: 'coral', price: '₹1,999/mo' },
  PRO: { name: 'Enterprise', limit: null, icon: Crown, color: 'warning', price: 'Custom' }
};

const STATUS_INFO = {
  ACTIVE: { label: 'Active', color: 'success', icon: CheckCircle },
  CANCELED: { label: 'Canceled', color: 'destructive', icon: Ban },
  EXPIRED: { label: 'Expired', color: 'warning', icon: Ban }
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedClient, setExpandedClient] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('session_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_name');
    localStorage.removeItem('client_id');
    localStorage.removeItem('clinic_name');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  // Stats
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    totalUsage: 0,
    byPlan: { BASIC: 0, GROWTH: 0, PRO: 0 }
  });

  // New client form (invite-based, no password needed)
  const [newClient, setNewClient] = useState({
    email: '',
    name: '',
    phone: '',
    clinic_name: '',
    clinic_speciality: '',
    city: '',
    google_review_link: '',
    plan: 'BASIC',
    send_invite: true, // Auto-send invite email
  });

  // New service form
  const [newService, setNewService] = useState({
    service_name: '',
    service_category: '',
    problem_it_solves: '',
    outcome_benefit: '',
    recommended_for: '',
    staff_name: '',
    staff_role: '',
    keywords: '',
    locality: '',
    city: '',
    // Tone
    review_tone: 'friendly',
    // SEO fields
    seo_keywords: '',
    target_audience: '',
    unique_selling_point: '',
  });

  const [addingServiceTo, setAddingServiceTo] = useState(null);
  const [deletingClient, setDeletingClient] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchLeads();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/admin/clients`);
      const data = await res.json();
      setClients(data);
      
      // Calculate stats
      const statsData = {
        totalClients: data.length,
        activeClients: data.filter(c => c.status === 'ACTIVE').length,
        totalUsage: data.reduce((sum, c) => sum + (c.usage_summary?.current_usage || 0), 0),
        byPlan: {
          BASIC: data.filter(c => c.plan === 'BASIC').length,
          GROWTH: data.filter(c => c.plan === 'GROWTH').length,
          PRO: data.filter(c => c.plan === 'PRO').length
        }
      };
      setStats(statsData);
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    setLeadsLoading(true);
    try {
      const res = await fetch(`${API}/admin/leads`);
      if (res.ok) {
        const data = await res.json();
        setLeads(data);
      }
    } catch (err) {
      console.error('Failed to load leads');
    } finally {
      setLeadsLoading(false);
    }
  };

  const createClient = async () => {
    if (!newClient.email || !newClient.clinic_name || !newClient.google_review_link) {
      toast.error('Email, Clinic Name, and Google Review Link are required');
      return;
    }

    try {
      const res = await fetch(`${API}/admin/client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newClient),
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create client');
      }
      
      const result = await res.json();
      if (result.invite_sent) {
        toast.success('Client created! Invite email sent.');
      } else {
        toast.success('Client created. Invite email could not be sent (check email settings).');
      }
      setNewClient({ 
        email: '', name: '', phone: '', clinic_name: '', 
        clinic_speciality: '', city: '', google_review_link: '', plan: 'BASIC', send_invite: true 
      });
      fetchClients();
    } catch (err) {
      toast.error(err.message || 'Failed to create client');
    }
  };

  const resendInvite = async (clientId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, action: 'resend_invite' }),
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to resend invite');
      }
      
      const result = await res.json();
      toast.success(result.message);
    } catch (err) {
      toast.error(err.message || 'Failed to resend invite');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteClient = async (clientId) => {
    setDeletingClient(clientId);
    try {
      const res = await fetch(`${API}/admin/client/${clientId}`, {
        method: 'DELETE',
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete client');
      }
      
      toast.success('Client deleted successfully');
      fetchClients();
    } catch (err) {
      toast.error(err.message || 'Failed to delete client');
    } finally {
      setDeletingClient(null);
    }
  };

  const handleDeleteClick = (clientId, clinicName) => {
    if (window.confirm(`Are you sure you want to permanently delete "${clinicName}"? This action cannot be undone.`)) {
      deleteClient(clientId);
    }
  };

  const performAdminAction = async (clientId, action, value = null) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, action, value }),
      });
      
      if (!res.ok) throw new Error('Action failed');
      
      const result = await res.json();
      toast.success(result.message);
      fetchClients();
    } catch (err) {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const addService = async (clientId) => {
    if (!newService.service_name) {
      toast.error('Service name is required');
      return;
    }

    try {
      const serviceData = {
        ...newService,
        keywords: newService.keywords ? newService.keywords.split(',').map(k => k.trim()).filter(k => k) : null,
        seo_keywords: newService.seo_keywords ? newService.seo_keywords.split(',').map(k => k.trim()).filter(k => k) : null,
      };
      
      const res = await fetch(`${API}/client/${clientId}/service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(serviceData),
      });
      
      if (!res.ok) throw new Error('Failed to add service');
      
      toast.success('Service added');
      setNewService({ 
        service_name: '', service_category: '', problem_it_solves: '', 
        outcome_benefit: '', recommended_for: '', staff_name: '', 
        staff_role: '', keywords: '', locality: '', city: '',
        review_tone: 'friendly',
        seo_keywords: '', target_audience: '', unique_selling_point: ''
      });
      setAddingServiceTo(null);
      fetchClients();
    } catch (err) {
      toast.error('Failed to add service');
    }
  };

  const toggleService = async (clientId, serviceId, enable) => {
    try {
      const action = enable ? 'enable' : 'disable';
      const res = await fetch(`${API}/client/${clientId}/service/${serviceId}/${action}`, {
        method: 'POST',
      });
      
      if (!res.ok) throw new Error('Failed to update service');
      toast.success(`Service ${enable ? 'enabled' : 'disabled'}`);
      fetchClients();
    } catch (err) {
      toast.error('Failed to update service');
    }
  };

  const getReviewPageUrl = (clientId) => {
    return `${window.location.origin}/review/${clientId}`;
  };

  const copyLink = async (clientId) => {
    const url = getReviewPageUrl(clientId);
    await navigator.clipboard.writeText(url);
    setCopiedLink(clientId);
    toast.success('Link copied');
    setTimeout(() => setCopiedLink(null), 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="w-full border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center shadow-glow">
                  <Star className="w-5 h-5 text-primary-foreground" />
                </div>
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold text-foreground">Socialxia Review</h1>
                <p className="text-xs text-muted-foreground">Admin Dashboard</p>
              </div>
            </Link>

            <div className="flex items-center gap-3">
              <Badge variant="destructive" className="flex items-center gap-1">
                <Settings className="w-3.5 h-3.5" />
                Super Admin
              </Badge>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card variant="glass">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.totalClients}</p>
                  <p className="text-sm text-muted-foreground">Total Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="glass">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.activeClients}</p>
                  <p className="text-sm text-muted-foreground">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="glass">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.totalUsage}</p>
                  <p className="text-sm text-muted-foreground">Reviews This Month</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="glass">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.byPlan.PRO}</p>
                  <p className="text-sm text-muted-foreground">Pro Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="clients" className="space-y-6">
          <TabsList>
            <TabsTrigger value="clients">All Clients</TabsTrigger>
            <TabsTrigger value="leads">
              Leads
              {leads.length > 0 && (
                <Badge variant="coral" className="ml-2 text-xs">{leads.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="add">Add Client</TabsTrigger>
          </TabsList>

          {/* Add Client Tab */}
          <TabsContent value="add">
            <Card variant="elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-primary" />
                  Add New Client
                </CardTitle>
                <CardDescription>
                  Create a new client account with ONE Google Review Link
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Email *</label>
                    <Input
                      type="email"
                      placeholder="client@example.com"
                      value={newClient.email}
                      onChange={(e) => setNewClient(p => ({ ...p, email: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Contact Name</label>
                    <Input
                      placeholder="John Doe"
                      value={newClient.name}
                      onChange={(e) => setNewClient(p => ({ ...p, name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Phone</label>
                    <Input
                      placeholder="+91 98765 43210"
                      value={newClient.phone}
                      onChange={(e) => setNewClient(p => ({ ...p, phone: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Clinic Name *</label>
                    <Input
                      placeholder="Sunrise Dental"
                      value={newClient.clinic_name}
                      onChange={(e) => setNewClient(p => ({ ...p, clinic_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Speciality</label>
                    <Input
                      placeholder="Cosmetic Dentistry"
                      value={newClient.clinic_speciality}
                      onChange={(e) => setNewClient(p => ({ ...p, clinic_speciality: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">City</label>
                    <Input
                      placeholder="Bengaluru"
                      value={newClient.city}
                      onChange={(e) => setNewClient(p => ({ ...p, city: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <label className="text-sm font-medium text-foreground">Google Review Link * (ONE per client)</label>
                    <Input
                      placeholder="https://g.page/r/xxx/review"
                      value={newClient.google_review_link}
                      onChange={(e) => setNewClient(p => ({ ...p, google_review_link: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <label className="text-sm font-medium text-foreground">Subscription Plan</label>
                    <Select value={newClient.plan} onValueChange={(value) => setNewClient(p => ({ ...p, plan: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PLAN_INFO).map(([key, info]) => (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <info.icon className="w-4 h-4" />
                              <span>{info.name} - {info.limit ? `${info.limit} reviews/mo` : 'Unlimited'} ({info.price})</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="premium" onClick={createClient}>
                  <Plus className="w-4 h-4" />
                  Create Client
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <Card variant="elevated">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      Waitlist Leads
                    </CardTitle>
                    <CardDescription>
                      People who requested access via the landing page
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchLeads} disabled={leadsLoading}>
                    <RefreshCw className={`w-4 h-4 ${leadsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {leadsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading leads...</div>
                ) : leads.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No leads yet. Share your landing page to collect waitlist signups.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Business Name</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Owner Name</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">City</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Phone</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plan</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((lead) => (
                          <tr key={lead.id} className="border-b border-border/50 hover:bg-muted/30">
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {new Date(lead.created_at).toLocaleDateString('en-IN', { 
                                day: '2-digit', 
                                month: 'short',
                                year: 'numeric'
                              })}
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-medium text-foreground">{lead.business_name}</span>
                            </td>
                            <td className="py-3 px-4 text-sm text-foreground">{lead.name}</td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">{lead.city}</td>
                            <td className="py-3 px-4">
                              <a href={`tel:${lead.phone}`} className="text-sm text-primary hover:underline flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {lead.phone}
                              </a>
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant={lead.plan === 'growth' ? 'coral' : lead.plan === 'enterprise' ? 'warning' : 'teal'}>
                                {lead.plan?.charAt(0).toUpperCase() + lead.plan?.slice(1)}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <a href={`mailto:${lead.email}`} className="text-sm text-primary hover:underline flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {lead.email}
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clients List Tab */}
          <TabsContent value="clients">
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-12 text-muted-foreground">Loading...</div>
              ) : clients.length === 0 ? (
                <Card variant="glass" className="text-center py-12">
                  <CardContent>
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No clients yet. Add your first client.</p>
                  </CardContent>
                </Card>
              ) : (
                clients.map((client) => {
                  const planInfo = PLAN_INFO[client.plan || 'BASIC'];
                  const PlanIcon = planInfo?.icon || Zap;
                  const statusInfo = STATUS_INFO[client.status || 'ACTIVE'];
                  const StatusIcon = statusInfo?.icon || CheckCircle;
                  const usage = client.usage_summary || {};
                  const inviteStatus = client.invite_status || 'accepted';
                  
                  return (
                    <Card key={client.id} variant="glass">
                      <CardHeader 
                        className="cursor-pointer" 
                        onClick={() => setExpandedClient(expandedClient === client.id ? null : client.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                                {client.clinic_name}
                                <Badge variant={statusInfo.color} className="text-xs">
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusInfo.label}
                                </Badge>
                                {inviteStatus === 'pending' && (
                                  <Badge variant="warning" className="text-xs" data-testid={`invite-badge-${client.id}`}>
                                    <Clock className="w-3 h-3 mr-1" />
                                    Invite Pending
                                  </Badge>
                                )}
                              </CardTitle>
                              <CardDescription className="flex items-center gap-3">
                                {client.email && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    {client.email}
                                  </span>
                                )}
                                {client.city && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {client.city}
                                  </span>
                                )}
                              </CardDescription>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={planInfo?.color || 'teal'} className="flex items-center gap-1">
                              <PlanIcon className="w-3 h-3" />
                              {planInfo?.name}
                            </Badge>
                            <Badge variant="outline">
                              {usage.current_usage || 0}/{usage.limit || '∞'}
                            </Badge>
                            {client.top_up_credits > 0 && (
                              <Badge variant="success">+{client.top_up_credits} credits</Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(client.id, client.clinic_name);
                              }}
                              disabled={deletingClient === client.id}
                              data-testid={`delete-client-${client.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            {expandedClient === client.id ? (
                              <ChevronUp className="w-5 h-5 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                      </CardHeader>

                      {expandedClient === client.id && (
                        <CardContent className="border-t border-border pt-6 space-y-6">
                          {/* Usage Progress */}
                          <div className="bg-muted/50 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-foreground">Monthly Usage</span>
                              <span className="text-sm text-muted-foreground">
                                {usage.unlimited ? 'Unlimited' : `${usage.current_usage || 0} / ${usage.limit} reviews`}
                                {client.top_up_credits > 0 && ` (+${client.top_up_credits} top-up)`}
                              </span>
                            </div>
                            {!usage.unlimited && (
                              <Progress 
                                value={((usage.current_usage || 0) / usage.limit) * 100} 
                                className={`h-2 ${usage.exceeded ? 'bg-destructive/20' : ''}`}
                              />
                            )}
                          </div>

                          {/* Admin Actions */}
                          <div className="bg-muted/50 rounded-xl p-4">
                            <h4 className="font-medium text-foreground mb-3">Admin Actions</h4>
                            <div className="flex flex-wrap gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => performAdminAction(client.id, 'reset_usage')}
                                disabled={actionLoading}
                              >
                                <RefreshCw className="w-4 h-4" />
                                Reset Usage
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => performAdminAction(client.id, 'grant_credits', '50')}
                                disabled={actionLoading}
                              >
                                <CreditCard className="w-4 h-4" />
                                Grant 50 Credits
                              </Button>
                              {client.status === 'ACTIVE' ? (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-destructive"
                                  onClick={() => performAdminAction(client.id, 'disable_account')}
                                  disabled={actionLoading}
                                >
                                  <Ban className="w-4 h-4" />
                                  Disable
                                </Button>
                              ) : (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-success"
                                  onClick={() => performAdminAction(client.id, 'enable_account')}
                                  disabled={actionLoading}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Enable
                                </Button>
                              )}
                              <Select 
                                value={client.plan} 
                                onValueChange={(value) => performAdminAction(client.id, 'change_plan', value)}
                              >
                                <SelectTrigger className="w-[180px] h-9">
                                  <SelectValue placeholder="Change Plan" />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(PLAN_INFO).map(([key, info]) => (
                                    <SelectItem key={key} value={key}>
                                      {info.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {inviteStatus === 'pending' && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => resendInvite(client.id)}
                                  disabled={actionLoading}
                                  data-testid={`resend-invite-${client.id}`}
                                >
                                  <Send className="w-4 h-4" />
                                  Resend Invite
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* QR Link */}
                          <div className="bg-muted/50 rounded-xl p-4">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                  <QrCode className="w-5 h-5 text-secondary" />
                                </div>
                                <div>
                                  <p className="font-medium text-foreground">Review Page Link</p>
                                  <p className="text-sm text-muted-foreground truncate max-w-md">
                                    {getReviewPageUrl(client.id)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => copyLink(client.id)}>
                                  {copiedLink === client.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                  Copy
                                </Button>
                                <Link to={`/review/${client.id}`} target="_blank">
                                  <Button variant="secondary" size="sm">
                                    <Eye className="w-4 h-4" />
                                    Preview
                                  </Button>
                                </Link>
                              </div>
                            </div>
                          </div>

                          {/* Services */}
                          <div>
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-medium text-foreground">Services ({client.services?.length || 0})</h4>
                              <Dialog open={addingServiceTo === client.id} onOpenChange={(open) => {
                                setAddingServiceTo(open ? client.id : null);
                              }}>
                                <DialogTrigger asChild>
                                  <Button variant="outline" size="sm">
                                    <Plus className="w-4 h-4" />
                                    Add Service
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                  <DialogHeader>
                                    <DialogTitle>Add New Service</DialogTitle>
                                    <DialogDescription>
                                      Add enriched service metadata for SEO-optimized reviews.
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="grid grid-cols-2 gap-4 py-4">
                                    <div className="space-y-2">
                                      <label className="text-sm font-medium">Service Name *</label>
                                      <Input
                                        placeholder="e.g., Sports Physiotherapy"
                                        value={newService.service_name}
                                        onChange={(e) => setNewService(p => ({ ...p, service_name: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <label className="text-sm font-medium">Category</label>
                                      <Input
                                        placeholder="e.g., Physiotherapy"
                                        value={newService.service_category}
                                        onChange={(e) => setNewService(p => ({ ...p, service_category: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                      <label className="text-sm font-medium">Problem It Solves</label>
                                      <Input
                                        placeholder="e.g., neck stiffness, lower back pain"
                                        value={newService.problem_it_solves}
                                        onChange={(e) => setNewService(p => ({ ...p, problem_it_solves: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                      <label className="text-sm font-medium">Outcome / Benefit</label>
                                      <Input
                                        placeholder="e.g., improved mobility, reduced pain"
                                        value={newService.outcome_benefit}
                                        onChange={(e) => setNewService(p => ({ ...p, outcome_benefit: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <label className="text-sm font-medium">Staff Name</label>
                                      <Input
                                        placeholder="e.g., Dr. Pooja"
                                        value={newService.staff_name}
                                        onChange={(e) => setNewService(p => ({ ...p, staff_name: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <label className="text-sm font-medium">Staff Role</label>
                                      <Input
                                        placeholder="e.g., Physiotherapist"
                                        value={newService.staff_role}
                                        onChange={(e) => setNewService(p => ({ ...p, staff_role: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <label className="text-sm font-medium">Locality</label>
                                      <Input
                                        placeholder="e.g., HSR Layout"
                                        value={newService.locality}
                                        onChange={(e) => setNewService(p => ({ ...p, locality: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <label className="text-sm font-medium">City</label>
                                      <Input
                                        placeholder="e.g., Bengaluru"
                                        value={newService.city}
                                        onChange={(e) => setNewService(p => ({ ...p, city: e.target.value }))}
                                      />
                                    </div>
                                    
                                    {/* Tone of Voice */}
                                    <div className="space-y-2 col-span-2">
                                      <label className="text-sm font-medium flex items-center gap-2">
                                        <Mic className="w-4 h-4 text-muted-foreground" />
                                        Preferred Review Tone
                                      </label>
                                      <Select 
                                        value={newService.review_tone} 
                                        onValueChange={(value) => setNewService(p => ({ ...p, review_tone: value }))}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select tone" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="professional">Professional</SelectItem>
                                          <SelectItem value="friendly">Friendly / Casual</SelectItem>
                                          <SelectItem value="enthusiastic">Enthusiastic</SelectItem>
                                          <SelectItem value="short">Short & Punchy</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    
                                    {/* SEO Fields */}
                                    <div className="col-span-2 border-t border-border pt-4 mt-2">
                                      <p className="text-sm font-medium text-primary flex items-center gap-2 mb-3">
                                        <Target className="w-4 h-4" />
                                        SEO Optimization Fields
                                      </p>
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                      <label className="text-sm font-medium flex items-center gap-2">
                                        <Sparkles className="w-4 h-4 text-muted-foreground" />
                                        SEO Focus Keywords
                                      </label>
                                      <Input
                                        placeholder="e.g., best pizza brooklyn, authentic italian (comma-separated)"
                                        value={newService.seo_keywords}
                                        onChange={(e) => setNewService(p => ({ ...p, seo_keywords: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                      <label className="text-sm font-medium flex items-center gap-2">
                                        <Users className="w-4 h-4 text-muted-foreground" />
                                        Target Audience
                                      </label>
                                      <Input
                                        placeholder="e.g., families, couples, business professionals"
                                        value={newService.target_audience}
                                        onChange={(e) => setNewService(p => ({ ...p, target_audience: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                      <label className="text-sm font-medium flex items-center gap-2">
                                        <Star className="w-4 h-4 text-muted-foreground" />
                                        Unique Selling Point
                                      </label>
                                      <Input
                                        placeholder="e.g., only wood-fired oven in the area, 20+ years experience"
                                        value={newService.unique_selling_point}
                                        onChange={(e) => setNewService(p => ({ ...p, unique_selling_point: e.target.value }))}
                                      />
                                    </div>
                                  </div>
                                  <DialogFooter>
                                    <DialogClose asChild>
                                      <Button variant="outline">Cancel</Button>
                                    </DialogClose>
                                    <Button variant="premium" onClick={() => addService(client.id)}>
                                      Add Service
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                            </div>

                            {client.services?.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-4">
                                No services configured.
                              </p>
                            ) : (
                              <div className="grid gap-2">
                                {client.services?.map((service) => (
                                  <div 
                                    key={service.id} 
                                    className={`flex items-center justify-between p-3 rounded-lg border ${
                                      service.is_active !== false ? 'bg-card' : 'bg-muted/30 opacity-60'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Heart className={`w-5 h-5 ${service.is_active !== false ? 'text-primary' : 'text-muted-foreground'}`} />
                                      <div>
                                        <p className="font-medium text-foreground">
                                          {service.service_name || service.name}
                                          {service.is_active === false && (
                                            <Badge variant="outline" className="ml-2 text-xs">Disabled</Badge>
                                          )}
                                        </p>
                                        {service.staff_name && (
                                          <p className="text-xs text-muted-foreground">
                                            {service.staff_name}{service.staff_role && ` (${service.staff_role})`}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => toggleService(client.id, service.id, service.is_active === false)}
                                    >
                                      {service.is_active !== false ? 'Disable' : 'Enable'}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};
